/**
 * USI-011 (ADR-011 § Structural commitment #4; design doc § Per-kind UI):
 * per-kind body for the `orphan-refund` triage item.
 *
 * Per design doc § Per-kind UI an orphan refund is a `ContributionRefund`
 * whose `ContributionId` is null — the auto-resolver at import time failed
 * to pick a parent. The treasurer has two choices:
 *
 *   1. **Pick parent** — choose a contribution from
 *      `item.suggestedOriginals` (the USI-007 aggregator's `(Amount,
 *      Date ±7d)` heuristic candidates) OR search-all for a parent the
 *      heuristic missed. Calls `POST /contribution-refunds/{id}/link`.
 *   2. **Skip to standalone** — record the deliberate choice to leave the
 *      refund as a standalone negative-amount row (Shape A: ContributionId
 *      stays null). Calls `POST /contribution-refunds/{id}/orphan-standalone`.
 *      Per ADR-011 § Negative consequences ("audit-defensibility narrows"),
 *      the semantically rich `RefundOrphanStandaloneChosen` audit row is
 *      the §17-5-8 evidence the trail relies on post-ADR-011.
 *
 * Keyboard model per CONVENTIONS.md § Keyboard shortcuts:
 *   - **P** = "Pick parent" → opens the search panel if no suggestion is
 *     highlighted, OR confirms the highlighted suggestion if one is.
 *   - **S** = "Skip to standalone" → overrides the modal-level Skip while
 *     this body is mounted (the modal's per-kind dispatcher routes S into
 *     this body's standalone callback instead of the default advance).
 *   - **Enter** = confirm the highlighted parent (same as clicking the
 *     row) when one is highlighted; no-op otherwise.
 *
 * Shape A pinned: the orphan refund stays as a `ContributionRefund` row
 * with `ContributionId = null` on the skip path. The audit row IS the
 * resolution; the modal advances chronologically per ADR-011 commitment #11.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ContributionDto,
  type TriageItem,
  ErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount } from '@/components/finance/utils';
import {
  useFinanceContributions,
  useLinkRefundToParent,
  useOrphanRefundStandalone,
  ContributionRefundLinkError,
} from '@/hooks/queries';
import type { RegisterConfirmFn } from './PendingContributionBody';
import { financeFormInputClass } from '@/components/finance/shared/financeControlClasses';

/**
 * Callback the modal passes to register the body's P-key "Pick parent"
 * handler. Same shape as USI-009/USI-010 registration slots; the modal's
 * keyboard hook fires the registered function when P is pressed outside a
 * text input.
 */
export type RegisterPickParentFn = (
  handler: { fn: () => void } | null,
) => void;

/**
 * Callback the modal passes to register the body's S-key override for
 * "Skip to standalone." When this body is mounted the modal routes S into
 * the registered standalone callback instead of the default
 * `onTriageSkip` (which would advance the queue). Null clears the
 * override so other kinds get the default Skip-advances behavior.
 */
export type RegisterStandaloneFn = (
  handler: { fn: () => void } | null,
) => void;

interface OrphanRefundBodyProps {
  // ATL-001: the kind re-keyed to the entity (`Refund`).
  item: Extract<TriageItem, { kind: 'Refund' }>;
  /** Fired after a successful Link or Standalone — advances the modal. */
  onClassified: () => void;
  /** Registers / clears the body's Enter-key confirm handler. */
  onRegisterConfirm: RegisterConfirmFn;
  /** Registers / clears the body's P-key "Pick parent" handler. */
  onRegisterPickParent: RegisterPickParentFn;
  /** Registers / clears the body's S-key skip-to-standalone override. */
  onRegisterStandalone: RegisterStandaloneFn;
}

export function OrphanRefundBody({
  item,
  onClassified,
  onRegisterConfirm,
  onRegisterPickParent,
  onRegisterStandalone,
}: OrphanRefundBodyProps) {
  const refund = item.refund;
  const suggestedOriginals = item.suggestedOriginals ?? [];

  const [highlightedParentId, setHighlightedParentId] = useState<string | null>(
    suggestedOriginals[0]?.id ?? null,
  );
  const [searchOpen, setSearchOpen] = useState(suggestedOriginals.length === 0);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: linkAsync, isPending: linkPending } =
    useLinkRefundToParent();
  const { mutateAsync: standaloneAsync, isPending: standalonePending } =
    useOrphanRefundStandalone();
  const isPending = linkPending || standalonePending;

  // The search-all fallback reuses the existing list endpoint with a small
  // pageSize. The hook's opt-in `enabled` gate prevents a network call on
  // every modal render — fire only after the search panel is open AND the
  // treasurer has typed at least two characters. The contactId filter is
  // left null per AC "search all" so the treasurer can find a parent the
  // heuristic missed, including across donors.
  const searchListEnabled = searchOpen && searchQuery.trim().length >= 2;
  const { data: searchResults, isFetching: searchFetching } =
    useFinanceContributions(
      { pageSize: 10 },
      { enabled: searchListEnabled },
    );

  // Local in-memory filter on the loaded page since the API doesn't have
  // a free-text search param in Phase 1. Sufficient for the low-volume
  // current scale per `project_low_active_usage_state.md`; a future search
  // endpoint can replace the client-side filter without changing the UI.
  const searchHits = useMemo(() => {
    if (!searchListEnabled || !searchResults?.items) return [] as ContributionDto[];
    const q = searchQuery.trim().toLowerCase();
    return (searchResults.items as ContributionDto[]).filter(
      (c) =>
        c.contactName.toLowerCase().includes(q) ||
        formatAmount(c.amount).toLowerCase().includes(q) ||
        c.date.includes(q),
    );
  }, [searchListEnabled, searchResults, searchQuery]);

  const handleLink = useCallback(
    async (originalContributionId: string) => {
      setErrorMessage(null);
      try {
        await linkAsync({
          refundId: refund.id,
          originalContributionId,
          concurrencyVersion: refund.concurrencyVersion,
          // §17-5-8 audit trail: ride the suggestion set into the audit
          // row so the trail records the candidates the treasurer
          // rejected before picking. Per ADR-011 § Negative consequences.
          suggestedOriginalIds: suggestedOriginals.map(
            (c: ContributionDto) => c.id,
          ),
        });
        onClassified();
      } catch (err) {
        if (err instanceof ContributionRefundLinkError) {
          if (err.code === ErrorCode.ContributionRefundLinkAlreadyLinked) {
            setErrorMessage(
              'This refund is already linked to a parent contribution.',
            );
            return;
          }
          if (isStaleWrite(err)) {
            setErrorMessage(
              'This refund was updated elsewhere — refresh to see the latest.',
            );
            return;
          }
        }
        setErrorMessage(
          err instanceof Error ? err.message : 'Failed to link refund to parent.',
        );
      }
    },
    [linkAsync, refund.id, refund.concurrencyVersion, onClassified, suggestedOriginals],
  );

  const handleStandalone = useCallback(async () => {
    setErrorMessage(null);
    try {
      await standaloneAsync({
        refundId: refund.id,
        concurrencyVersion: refund.concurrencyVersion,
        // Mirror the link path's audit-trail discipline: record the
        // candidates the treasurer was offered before choosing standalone
        // so a regulator's "why is there no parent?" question has a
        // grounded answer.
        suggestedOriginalIds: suggestedOriginals.map(
          (c: ContributionDto) => c.id,
        ),
      });
      onClassified();
    } catch (err) {
      if (err instanceof ContributionRefundLinkError) {
        if (err.code === ErrorCode.ContributionRefundLinkAlreadyLinked) {
          setErrorMessage(
            'This refund is already linked to a parent contribution.',
          );
          return;
        }
        if (isStaleWrite(err)) {
          setErrorMessage(
            'This refund was updated elsewhere — refresh to see the latest.',
          );
          return;
        }
      }
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to record standalone choice.',
      );
    }
  }, [standaloneAsync, refund.id, refund.concurrencyVersion, onClassified, suggestedOriginals]);

  // useRef stabilizes the handler identities across renders so the modal's
  // registration effects don't infinite-loop. Same pattern as
  // PendingContributionBody + MissingInfoBody — see those files for the
  // load-bearing rationale.
  const handleConfirmRef = useRef<() => void>(() => {});
  const handlePickRef = useRef<() => void>(() => {});
  const handleStandaloneRef = useRef(handleStandalone);

  useEffect(() => {
    handleStandaloneRef.current = handleStandalone;
  }, [handleStandalone]);

  // Confirm (Enter) routes to the highlighted parent's link mutation when
  // one is highlighted. No-op otherwise (the treasurer can press S for
  // standalone or click into the search panel).
  useEffect(() => {
    handleConfirmRef.current = () => {
      if (highlightedParentId) {
        void handleLink(highlightedParentId);
      }
    };
  }, [highlightedParentId, handleLink]);

  // P key opens the search panel when none of the suggestions are
  // highlighted (or there are no suggestions). When a suggestion IS
  // highlighted, P confirms it — same as Enter, but reachable from a
  // dedicated key per CONVENTIONS.md.
  useEffect(() => {
    handlePickRef.current = () => {
      if (highlightedParentId) {
        void handleLink(highlightedParentId);
        return;
      }
      setSearchOpen(true);
    };
  }, [highlightedParentId, handleLink]);

  // Registration: only when no mutation is in flight (otherwise Enter / P
  // / S could fire a second mutation before the first resolves).
  useEffect(() => {
    if (isPending) {
      onRegisterConfirm(null);
      return;
    }
    onRegisterConfirm({ fn: () => handleConfirmRef.current() });
    return () => onRegisterConfirm(null);
  }, [isPending, onRegisterConfirm]);

  useEffect(() => {
    if (isPending) {
      onRegisterPickParent(null);
      return;
    }
    onRegisterPickParent({ fn: () => handlePickRef.current() });
    return () => onRegisterPickParent(null);
  }, [isPending, onRegisterPickParent]);

  useEffect(() => {
    if (isPending) {
      onRegisterStandalone(null);
      return;
    }
    onRegisterStandalone({ fn: () => handleStandaloneRef.current() });
    return () => onRegisterStandalone(null);
  }, [isPending, onRegisterStandalone]);

  return (
    <div data-testid="orphan-refund-body" className="flex flex-col gap-4">
      <RefundSummary
        amount={refund.amount}
        refundedAt={refund.refundedAt}
        reason={refund.reason}
      />

      {suggestedOriginals.length > 0 && (
        <section
          data-testid="orphan-refund-suggestions"
          className="flex flex-col gap-2"
        >
          <p className="text-xs font-medium text-muted-foreground">
            Suggested original contributions (matching amount, within 7 days)
          </p>
          <div className="flex flex-col gap-1">
            {suggestedOriginals.map((c) => (
              <SuggestionRow
                key={c.id}
                contribution={c}
                highlighted={c.id === highlightedParentId}
                onHighlight={() => setHighlightedParentId(c.id)}
                onPick={() => handleLink(c.id)}
                disabled={isPending}
              />
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground">
            Or search all contributions
          </p>
          {!searchOpen && (
            <Button
              type="button"
              variant="ghost"
              data-testid="orphan-refund-open-search"
              onClick={() => setSearchOpen(true)}
            >
              Search
            </Button>
          )}
        </div>
        {searchOpen && (
          <>
            <input
              data-testid="orphan-refund-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
              placeholder="Search by donor name, amount, or date"
            />
            {searchListEnabled && (
              <div
                data-testid="orphan-refund-search-results"
                className="flex flex-col gap-1"
              >
                {searchFetching && (
                  <p className="text-xs text-muted-foreground">Searching…</p>
                )}
                {!searchFetching && searchHits.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No matches yet. Try a different term.
                  </p>
                )}
                {searchHits.map((c) => (
                  <SuggestionRow
                    key={c.id}
                    contribution={c}
                    highlighted={c.id === highlightedParentId}
                    onHighlight={() => setHighlightedParentId(c.id)}
                    onPick={() => handleLink(c.id)}
                    disabled={isPending}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {errorMessage && (
        <div
          role="alert"
          data-testid="orphan-refund-error"
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t pt-3">
        <Button
          type="button"
          variant="outline"
          data-testid="orphan-refund-standalone"
          disabled={isPending}
          onClick={handleStandalone}
        >
          Skip to standalone
        </Button>
      </div>
    </div>
  );
}

function RefundSummary({
  amount,
  refundedAt,
  reason,
}: {
  amount: number;
  refundedAt: string;
  reason: string;
}) {
  return (
    <div
      data-testid="orphan-refund-summary"
      className="rounded-md border bg-muted/30 p-3 text-sm"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">
          {refundedAt}
        </span>
        <span className="font-semibold">{formatAmount(amount)}</span>
        <span className="ml-auto rounded bg-background px-2 py-0.5 text-xs text-muted-foreground">
          Orphan refund
        </span>
      </div>
      {reason && (
        <p className="mt-1 text-xs text-muted-foreground">{reason}</p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        No parent contribution was auto-matched. Pick a parent below or skip
        to leave this refund as a standalone negative-amount row.
      </p>
    </div>
  );
}

function SuggestionRow({
  contribution,
  highlighted,
  onHighlight,
  onPick,
  disabled,
}: {
  contribution: ContributionDto;
  highlighted: boolean;
  onHighlight: () => void;
  onPick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={`orphan-refund-suggestion-${contribution.id}`}
      data-highlighted={highlighted}
      disabled={disabled}
      onClick={onPick}
      onMouseEnter={onHighlight}
      onFocus={onHighlight}
      className={`flex items-center justify-between rounded border px-3 py-2 text-left text-sm transition-colors ${
        highlighted
          ? 'border-primary bg-primary/5'
          : 'border-input bg-background hover:bg-muted/30'
      } disabled:opacity-60`}
    >
      <span className="flex flex-col">
        <span className="font-medium">{contribution.contactName}</span>
        <span className="text-xs text-muted-foreground">
          {contribution.date}
        </span>
      </span>
      <span className="font-semibold">{formatAmount(contribution.amount)}</span>
    </button>
  );
}
